import { NamedProperty } from "sprint-common-web";

import { GetMuxSpecificationByIdResponse } from "../../../common/api/orders-api/mux-specification/responses/GetMuxSpecificationByIdResponse";
import {
    GpacMuxSpecificationItemResponse,
    ManzanitaMuxSpecificationItemResponse
} from "../../../common/api/orders-api/mux-specification/responses/MuxSpecificationItemResponse";
import { MuxerType } from "../../../common/types/MuxerType";
import { LanguageSpecificationType } from "../../common/types/LanguageSpecificationType";

export type MuxSpecificationDashboardItem = ManzanitaMuxSpecificationDashboardItem | GpacMuxSpecificationDashboardItem;

export interface ManzanitaMuxSpecificationDashboardItem extends BaseMuxSpecificationDashboardItem {
    muxerType: MuxerType.Manzanita;
    pcrPid: string;
    pmtPid: string;
    vbr?: boolean;
    autoDescrAvc?: boolean;
    videoPid: string;
    pesAlign?: boolean;
    maxDecodeDelay?: number;
    languageSpecification: LanguageSpecificationDashboardItem;
}

export interface GpacMuxSpecificationDashboardItem extends BaseMuxSpecificationDashboardItem {
    muxerType: MuxerType.GPAC;
    command: string;
    soundtracksCommand: string;
    subtitlesCommand: string;
}

export type LanguageSpecificationDashboardItem =
    | DictionaryLanguageSpecificationDashboardItem
    | SequentialLanguageSpecificationDashboardItem;

export interface DictionaryLanguageSpecificationDashboardItem {
    type: LanguageSpecificationType.Dictionary;
    soundtrackLanguagePidDictionary: Record<LanguageId, PID>;
    subtitleLanguagePidDictionary: Record<LanguageId, PID>;
}

type LanguageId = string;
type PID = string | undefined;

export interface SequentialLanguageSpecificationDashboardItem {
    type: LanguageSpecificationType.Sequential;
}

export type BaseMuxSpecificationDashboardItem = NamedProperty;

export const defaultMuxSpecificationDashboardItem: MuxSpecificationDashboardItem = {
    id: "",
    name: "",
    muxerType: MuxerType.Manzanita,
    pcrPid: "",
    pmtPid: "",
    vbr: false,
    autoDescrAvc: false,
    videoPid: "",
    pesAlign: false,
    maxDecodeDelay: 0,
    languageSpecification: { type: LanguageSpecificationType.Sequential }
};

export const createMuxSpecificationDashboardItem = (
    response: GetMuxSpecificationByIdResponse
): MuxSpecificationDashboardItem => {
    if (response.muxerType === MuxerType.Manzanita) {
        return createManzanitaMuxSpecificationDashboardItem(response);
    } else if (response.muxerType === MuxerType.GPAC) {
        return createGpacMuxSpecificationDashboardItem(response);
    }

    throw new Error("Unsupported muxer type ");
};

const createManzanitaMuxSpecificationDashboardItem = (
    response: ManzanitaMuxSpecificationItemResponse
): ManzanitaMuxSpecificationDashboardItem => ({
    id: response.id,
    name: response.name,
    muxerType: MuxerType.Manzanita,
    pcrPid: response.pcrPid,
    pmtPid: response.pmtPid,
    vbr: response.vbr,
    autoDescrAvc: response.autoDescrAvc,
    videoPid: response.videoPid,
    pesAlign: response.pesAlign,
    maxDecodeDelay: response.maxDecodeDelay,
    languageSpecification:
        response.languageSpecification.type === LanguageSpecificationType.Dictionary
            ? {
                  type: LanguageSpecificationType.Dictionary,
                  soundtrackLanguagePidDictionary: response.languageSpecification.soundtrackLanguagePidDictionary,
                  subtitleLanguagePidDictionary: response.languageSpecification.subtitleLanguagePidDictionary
              }
            : { type: LanguageSpecificationType.Sequential }
});

const createGpacMuxSpecificationDashboardItem = (
    response: GpacMuxSpecificationItemResponse
): GpacMuxSpecificationDashboardItem => ({
    id: response.id,
    name: response.name,
    muxerType: MuxerType.GPAC,
    command: response.command,
    soundtracksCommand: response.soundtracksCommand,
    subtitlesCommand: response.subtitlesCommand
});

export const ensureIsManzanitaMuxSpecification: (
    value: MuxSpecificationDashboardItem
) => asserts value is ManzanitaMuxSpecificationDashboardItem = (
    value: MuxSpecificationDashboardItem
): asserts value is ManzanitaMuxSpecificationDashboardItem => {
    if (value.muxerType !== MuxerType.Manzanita) {
        throw new Error("Muxer type should be Manzanita");
    }
};

export const ensureIsGpacMuxSpecification: (
    value: MuxSpecificationDashboardItem
) => asserts value is GpacMuxSpecificationDashboardItem = (
    value: MuxSpecificationDashboardItem
): asserts value is GpacMuxSpecificationDashboardItem => {
    if (value.muxerType !== MuxerType.GPAC) {
        throw new Error("Muxer type should be Gpac");
    }
};
